import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';

const marketActionCardCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{position:relative;outline:none;display:block;padding:var(--action-card-padding-vertical-size) var(--action-card-padding-horizontal-size);border-radius:var(--action-card-border-radius);background-color:var(--action-card-background-color);box-shadow:inset\n    0\n    0\n    0\n    var(--action-card-normal-state-unselected-value-border-width)\n    var(--action-card-normal-state-unselected-value-border-color);opacity:var(--action-card-normal-state-content-opacity);cursor:pointer}:host(:hover){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-hover-state-unselected-value-border-width)\n      var(--action-card-hover-state-unselected-value-border-color)}:host(:active){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-active-state-unselected-value-border-width)\n      var(--action-card-active-state-unselected-value-border-color)}:host([disabled]){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-disabled-state-unselected-value-border-width)\n      var(--action-card-disabled-state-unselected-value-border-color);opacity:var(--action-card-disabled-state-content-opacity)}:host([selected]){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-normal-state-selected-value-border-width)\n      var(--action-card-normal-state-selected-value-border-color)}:host([selected]:hover){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-hover-state-selected-value-border-width)\n      var(--action-card-hover-state-selected-value-border-color)}:host([selected]:active){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-active-state-selected-value-border-width)\n      var(--action-card-active-state-selected-value-border-color)}:host([selected][disabled]){box-shadow:inset\n      0\n      0\n      0\n      var(--action-card-disabled-state-selected-value-border-width)\n      var(--action-card-disabled-state-selected-value-border-color)}:host(.has-slotted-row){padding:0 var(--row-background-horizontal-outset-padding)}:host(.has-slotted-row) ::slotted(.market-row){background-color:transparent}:host::after{--focus-ring-position:calc(-1 * (var(--action-card-focus-ring-border-size) + var(--action-card-focus-ring-buffer-size) + var(--action-card-focus-ring-border-size)));content:'';position:absolute;top:var(--focus-ring-position);right:var(--focus-ring-position);bottom:var(--focus-ring-position);left:var(--focus-ring-position);display:block;border:var(--action-card-focus-ring-border-size) solid var(--action-card-focus-ring-color);border-radius:calc(var(--action-card-border-radius) + var(--action-card-focus-ring-buffer-size) * 2);opacity:0;pointer-events:none;transition:opacity 0.2s;content:'';position:absolute;display:block}:host(.focus-visible)::after{opacity:0.5}";

const MarketActionCard = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketCardSelected = createEvent(this, "marketCardSelected", 7);
    this.marketCardDeselected = createEvent(this, "marketCardDeselected", 7);
    this.selected = false;
    this.disabled = false;
    this.value = undefined;
    this.transient = false;
    this.row = null;
  }
  /**
   * When rows are slotted into cards, we want to catch their selection events
   * and emit our own, so that the containing `market-list` only gets one set
   * of selection events.
   */
  handleRowSelection(e) {
    this.select();
    // Prevent `marketRowSelected` from bubbling up to containing lists, since we expect
    // them to listen to our card selection events instead.
    e.stopPropagation();
  }
  /**
   * When rows are slotted into cards, we want to catch their selection events
   * and emit our own, so that the containing `market-list` only gets one set
   * of selection events.
   */
  handleRowDeselection(e) {
    this.deselect();
    // Prevent `marketRowSelected` from bubbling up to containing lists, since we expect
    // them to listen to our card selection events instead.
    e.stopPropagation();
  }
  /**
   * Used for setting the selection state to true without emitting events.
   * Useful for scenarios where another component (ex. `<market-list>`) needs
   * to sync state with slotted `<market-action-card>`s.
   */
  silentlySelect() {
    this.selected = true;
    if (this.row) {
      this.row.silentlySelect();
    }
    return Promise.resolve();
  }
  /**
   * Set `selected` to `true` and emit `marketCardSelected`. Generally speaking,
   * it is preferable to avoid using this method from outside this component
   * and allow `market-action-card` to manage its own selection state based on user
   * interaction. It should only be used for parent components that need to
   * manage a group of rows, such as `market-list`.
   */
  select() {
    this.selected = true;
    if (this.row) {
      this.row.silentlySelect();
    }
    this.marketCardSelected.emit(this.buildEventDetail());
    return Promise.resolve();
  }
  /**
   * Set `selected` to `false` and emit `marketCardDeselected`. Generally speaking,
   * it is preferable to avoid using this method from outside this component
   * and allow `market-action-card` to manage its own selection state based on user
   * interaction. It should only be used for parent components that need to
   * manage a group of rows, such as `market-list`.
   */
  deselect() {
    this.selected = false;
    if (this.row) {
      this.row.silentlyDeselect();
    }
    this.marketCardDeselected.emit(this.buildEventDetail());
    return Promise.resolve();
  }
  /**
   * Set `selected` to `false`. Generally speaking,
   * it is preferable to avoid using this method from outside this component
   * and allow `market-action-card` to manage its own selection state based on user
   * interaction. It should only be used for parent components that need to
   * manage a group of rows, such as `market-list`.
   */
  silentlyDeselect() {
    this.selected = false;
    if (this.row) {
      this.row.silentlyDeselect();
    }
    return Promise.resolve();
  }
  buildEventDetail() {
    return {
      value: this.value,
    };
  }
  handleClick() {
    // Rows handle selected state when slotted. The only way you can click directly on
    // the card is by clicking the border, and we want to just ignore that edge case.
    if (this.disabled || this.transient || this.row) {
      return;
    }
    if (!this.selected) {
      this.select();
    }
    else {
      this.deselect();
    }
  }
  handleKeydown(e) {
    if (e.key === 'Enter') {
      if (this.row) {
        // if slotted row is present, click it
        this.row.click();
      }
      else {
        this.handleClick();
      }
    }
  }
  componentWillRender() {
    const row = this.el.querySelector(getNamespacedTagFor('market-row'));
    if (row) {
      this.row = row;
      if (!row.interactive) {
        // Action cards are interactive, so rows slotted into them should be as well
        row.interactive = true;
      }
      this.el.classList.add('has-slotted-row');
      if (this.selected) {
        this.row.silentlySelect();
      }
    }
  }
  componentDidRender() {
    // slotted rows inside action cards should not be able to receive focus because
    // they are controlled by interaction w/ the action card
    if (this.row) {
      this.row.setAttribute('tabIndex', null);
    }
  }
  render() {
    return (h(Host, { class: "market-action-card", tabindex: this.disabled ? null : '0', role: "option", "aria-selected": this.selected, onClick: () => this.handleClick(), onKeydown: (e) => this.handleKeydown(e) }, h("slot", null)));
  }
  get el() { return getElement(this); }
};
MarketActionCard.style = marketActionCardCss;

export { MarketActionCard as market_action_card };
